import { BANNER_OFFSET_HEIGHT, BANNER_OFFSET_TOP } from '@common/components/dunning-banner/banner-offset';
import UserControlPanel from "@common-components/user-control-panel/user-control-panel";
import WorkspaceDropDown from "@common-components/workspace-dropdown/workspace-dropdown";
import { isBoardPanelDrawerOpen } from "@common-reducers/BoardsSelectors";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { mainSliceActions } from "@common-reducers/MainReducer";
import { useIsDesktop } from "@common/hooks/use-is-mobile";
import ExtensionSidebarContactList from '@common/components/scheduled-contact-list/extension-sidebar-contact-list';
import ExtensionSidebarSearchBox from '@common/components/scheduled-contact-list/extension-sidebar-search-box';

import { isExtensionContext } from "@common/services/detect-context";
import { useSafeLocation } from "@common/services/utils";
import { Drawer, Stack, useTheme } from "@mui/material";
import {
  ReactElement,
  memo,
  useState,
  useCallback,
} from "react";
import BoardsList from "./boards-list";
import NewBoardButton2 from "./new-board-button2";
import SideBarHeader from './side-bar-header';
import { resolveDrawerAnchor } from "./drawer-anchor";

const SIDEBAR_WIDTH_EXPANDED = "300px";
const SIDEBAR_WIDTH_MINIMIZED = "72px";

function BoardsPanel({ activeExtensionView }: { activeExtensionView?: string }): ReactElement {
  const dispatch = useAppDispatch();
  const drawerIsOpen = useAppSelector((state) => isBoardPanelDrawerOpen(state))
  const reduxDirection = useAppSelector((state) => state.UserReducer.direction);
  const theme = useTheme();
  // App-wide mobile nav drawer open state (temporary drawer on mobile).
  const isMobileMenuOpen = useAppSelector((state) => state.MainReducer.isMobileMenuOpen);

  const isDesktop = useIsDesktop();
  const closeMobileMenu = useCallback(() => {
    dispatch(mainSliceActions.toggleMobileMenu(false));
  }, [dispatch]);
  const location = useSafeLocation();
  const isExtension = isExtensionContext();
  const isCalendarView = activeExtensionView === "calendar" || location.pathname.includes("/calendar");
  const forceLeftCalendarSidebar = isExtension && isCalendarView;

  // RTL nav direction source is CONTEXT-SPECIFIC:
  //  - WEB app: use the live MUI `theme.direction` (BTThemeProvider keeps it in sync with the
  //    document `dir`). Redux `UserReducer.direction` defaults to "ltr" and lags the DOM in some
  //    flows, which left this permanent Drawer anchored LEFT in RTL (position:fixed; left:0),
  //    overlapping the calendar with an empty right gutter. This is the fix.
  //  - EXTENSION: the shadow-DOM MUI theme is hard-coded `direction:"ltr"` (theme.ts), and rtl.ts
  //    deliberately keeps the computed direction in REDUX for the extension (without mutating
  //    WhatsApp's DOM). So keep reading redux there — using theme.direction would always resolve
  //    LTR and break RTL for extension users. Byte-for-byte unchanged from before this PR.
  const direction: "ltr" | "rtl" = isExtension
    ? (reduxDirection === "rtl" ? "rtl" : "ltr")
    : (theme.direction === "rtl" ? "rtl" : "ltr");
  const drawerAnchor = resolveDrawerAnchor(direction, forceLeftCalendarSidebar);

  const [isSidebarMinimized, setIsSidebarMinimized] = useState(() => {
    try {
      return localStorage.getItem('sidebar-minimized') === 'true';
    } catch {
      return false;
    }
  });
  const toggleSidebarMinimized = useCallback(() => {
    setIsSidebarMinimized(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebar-minimized', String(next)); } catch {}
      return next;
    });
  }, []);

  // Minimize is a DESKTOP-only affordance. On mobile the drawer is a full-width
  // temporary overlay, so a leaked `sidebar-minimized` flag must never collapse
  // its content (that made the open menu look broken with no way back).
  const effectiveMinimized = isDesktop ? isSidebarMinimized : false;

  const sidebarWidth = isDesktop
    ? (drawerIsOpen ? (isSidebarMinimized ? SIDEBAR_WIDTH_MINIMIZED : SIDEBAR_WIDTH_EXPANDED) : '80px')
    : '80px';

  return (
    <>
      <Drawer
        sx={{
          width: sidebarWidth,
          transition: 'all 0.3s ease',
          [`& .MuiDrawer-paper`]: {
            background: 'var(--nav-bg)',
            transition: 'all 0.3s ease',
            // Border + shadow key off the actual drawer SIDE, not raw direction: the content-facing
            // edge is opposite the anchor. This stays correct in the forceLeft (extension calendar)
            // case, where the drawer is anchored left even under RTL.
            ...(drawerAnchor === 'right'
              ? { borderLeft: '1px solid var(--nav-border)', boxShadow: '-2px 0 12px rgba(0, 0, 0, 0.08)' }
              : { borderRight: '1px solid var(--nav-border)', boxShadow: '2px 0 12px rgba(0, 0, 0, 0.08)' }),
            width: isDesktop ? sidebarWidth : '100%',
            overflow: 'hidden',
            color: 'var(--nav-text)',
            // MUI's paper is position:fixed; top:0, so it ignores the dunning banner that pushes the overlay down.
            ...(forceLeftCalendarSidebar ? { left: 0, right: 'auto', top: BANNER_OFFSET_TOP, height: BANNER_OFFSET_HEIGHT } : {}),
          },
        }}
        variant={isDesktop ? "permanent" : 'temporary'}
        open={isDesktop ? drawerIsOpen : isMobileMenuOpen}
        onClose={closeMobileMenu}
        anchor={drawerAnchor}
      >

        <Stack
          sx={{
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Fixed Header */}
          <Stack sx={{ flexShrink: 0 }}>
            <SideBarHeader
              drawerIsOpen={drawerIsOpen}
              direction={direction}
              isMinimized={effectiveMinimized}
              onToggleMinimize={toggleSidebarMinimized}
              onNavigate={!isDesktop ? closeMobileMenu : undefined}
            />
          </Stack>

          <Stack sx={{
            opacity: effectiveMinimized ? 0 : 1,
            maxHeight: effectiveMinimized ? 0 : '9999px',
            overflow: 'hidden',
            transition: 'opacity 0.25s ease, max-height 0.3s ease',
            flexShrink: 0,
          }}>
            {(activeExtensionView === "calendar" || location.pathname.includes("/calendar")) &&
              <Stack sx={{ width: "100%" }} >
                <ExtensionSidebarSearchBox />
              </Stack>
            }

            {location.pathname.includes("/tasks") &&
              <Stack sx={{ width: "100%" }} mt={2} spacing={2} >
                <WorkspaceDropDown drawerIsOpen={drawerIsOpen} />
                <NewBoardButton2 drawerIsOpen={drawerIsOpen} />
              </Stack>
            }
          </Stack>

          {/* Scrollable Main Content */}
          <Stack
            sx={{
              flexGrow: effectiveMinimized ? 0 : 1,
              overflow: effectiveMinimized ? 'hidden' : 'auto',
              opacity: effectiveMinimized ? 0 : 1,
              transition: 'opacity 0.25s ease',
            }}
          >
            {!effectiveMinimized && (
              <>
                {(activeExtensionView === "calendar" || location.pathname.includes("/calendar")) &&
                  <ExtensionSidebarContactList />
                }

                {location.pathname.includes("/tasks") &&
                  <Stack
                    sx={{ width: "100%", flexGrow: 1, height: "100%", overflow: "hidden" }}
                    mt={2}
                    spacing={2}
                    onClick={!isDesktop ? closeMobileMenu : undefined}
                  >
                    <BoardsList drawerIsOpen={drawerIsOpen} />
                  </Stack>
                }
              </>
            )}
          </Stack>

          {/* Fixed Footer */}
          {!isExtension && (
            <Stack sx={{ flexShrink: 0, marginTop: 'auto' }}>
              <UserControlPanel isSideMenuOpen={drawerIsOpen && !isSidebarMinimized} />
            </Stack>
          )}
        </Stack>
      </Drawer>

    </>
  );
}

export default memo(BoardsPanel);
